// src/stores/service.store.ts
import { create } from "zustand";
import type { ServiceSearchResponse, ServiceSearchItem } from "../models/service_search.model.ts";
import { normalizeRelatedServices } from "../mappers/service.mapper.ts";
import type {
  ServiceHomeResponse,
  MainBanner,
  Category,
  SubCategory,
  UmmahPosts,
  SubBanner,
  RelatedServiceGroup,
  HomeNotice,
} from "../models/service_home.model.ts";
import type { ServiceDetailResponse } from "../models/service_detail.model.ts";

type SearchCacheItem = {
  authKey: string;
  data: ServiceSearchResponse;
  fetchedAt: number;
};

type DetailCacheItem = {
  authKey: string;
  data: ServiceDetailResponse;
  fetchedAt: number;
};

interface ServiceStoreState {
  data: ServiceHomeResponse | null;

  main_banners: MainBanner[];
  categories: Category[];
  sub_categories: SubCategory[];
  ummah_posts: UmmahPosts | null;
  sub_banners: SubBanner[];
  related_services: RelatedServiceGroup[];
  home_notice: HomeNotice | null;
  new_notice: boolean;

  recommended_keywords: string[];
  search_histories: string[];
  setSearchHome: (keywords: string[], histories: string[]) => void;
  removeSearchHistories: (keywords?: string[]) => void;

  dashboardAuthKey: string | null;
  hasDashboard: boolean;

  category_services_by_id: Record<
    string,
    {
      authKey: string;
      data: {
        search_filter: {
          location_tags: string[];
          min_price?: string;
          max_price?: string;
          count: number;
        };
        related_services: RelatedServiceGroup[];
      };
      fetchedAt: number;
    }
  >;

  // Search cache
  search_services_by_key: Record<string, SearchCacheItem>;

  // Detail cache
  service_detail_by_uuid: Record<string, DetailCacheItem>;

  isLoading: boolean;
  error: string | null;

  setLoading: (v: boolean) => void;
  setError: (msg: string | null) => void;

  dashboardLoading: boolean;
  dashboardError: string | null;

  categoryLoadingById: Record<string, boolean>;
  categoryErrorById: Record<string, string | null>;

  searchLoadingByKey: Record<string, boolean>;
  searchErrorByKey: Record<string, string | null>;

  detailLoadingByUuid: Record<string, boolean>;
  detailErrorByUuid: Record<string, string | null>;

  setDashboardLoading: (v: boolean) => void;
  setDashboardError: (msg: string | null) => void;

  setCategoryLoading: (categoryId: string, v: boolean) => void;
  setCategoryError: (categoryId: string, msg: string | null) => void;

  setSearchLoading: (cacheKey: string, v: boolean) => void;
  setSearchError: (cacheKey: string, msg: string | null) => void;

  setDetailLoading: (uuid: string, v: boolean) => void;
  setDetailError: (uuid: string, msg: string | null) => void;

  // ----- Actions -----
  setFromApi: (payload: ServiceHomeResponse, authKey: string) => void;
  clear: () => void;

  getActiveMainBanners: () => MainBanner[];
  getActiveSubBanners: () => SubBanner[];
  getActiveCategories: () => Category[];
  getSubCategoriesByCategoryId: (categoryId: number) => SubCategory[];

  setCategoryServices: (
    categoryId: string,
    authKey: string,
    payload: any,
  ) => void;

  clearCategoryServices: () => void;

  // Search
  hasSearchServices: (cacheKey: string, authKey: string) => boolean;
  getSearchServices: (cacheKey: string) => ServiceSearchResponse | null;
  canFetchMoreSearchServices: (cacheKey: string) => boolean;
  upsertSearchServices: (cacheKey: string, authKey: string, payload: ServiceSearchResponse, page: number) => void;
  clearSearchServices: () => void;

  // Detail
  hasServiceDetail: (uuid: string, authKey: string) => boolean;
  getServiceDetail: (uuid: string) => ServiceDetailResponse | null;
  setServiceDetail: (uuid: string, authKey: string, payload: ServiceDetailResponse) => void;
  clearServiceDetail: () => void;
  /** 통화 변경 시 호출 — 금액이 담긴 캐시는 전부 이전 통화 응답이다. */
  clearPricedCaches: () => void;
}

const emptyState = {
  data: null as ServiceHomeResponse | null,
  main_banners: [] as MainBanner[],
  categories: [] as Category[],
  sub_categories: [] as SubCategory[],
  ummah_posts: null as UmmahPosts | null,
  sub_banners: [] as SubBanner[],
  related_services: [] as RelatedServiceGroup[],
  home_notice: null as HomeNotice | null,
  new_notice: false,
  recommended_keywords: [] as string[],
  search_histories: [] as string[],

  dashboardAuthKey: null as string | null,
  hasDashboard: false,

  category_services_by_id: {} as ServiceStoreState["category_services_by_id"],
  search_services_by_key: {} as Record<string, SearchCacheItem>,
  service_detail_by_uuid: {} as Record<string, DetailCacheItem>,

  // feature loading/error
  dashboardLoading: false,
  dashboardError: null as string | null,

  categoryLoadingById: {} as Record<string, boolean>,
  categoryErrorById: {} as Record<string, string | null>,

  searchLoadingByKey: {} as Record<string, boolean>,
  searchErrorByKey: {} as Record<string, string | null>,

  detailLoadingByUuid: {} as Record<string, boolean>,
  detailErrorByUuid: {} as Record<string, string | null>,
};

export const useServiceStore = create<ServiceStoreState>((set, get) => ({
  ...emptyState,

  isLoading: false,
  error: null,

  setLoading: (v) => set({ isLoading: v }),
  setError: (msg) => set({ error: msg }),

  setDashboardLoading: (v) => set({ dashboardLoading: v, isLoading: v }),
  setDashboardError: (msg) => set({ dashboardError: msg, error: msg }),

  setCategoryLoading: (categoryId, v) =>
    set((state) => ({
      categoryLoadingById: { ...state.categoryLoadingById, [categoryId]: v },
      isLoading: v,
    })),

  setCategoryError: (categoryId, msg) =>
    set((state) => ({
      categoryErrorById: { ...state.categoryErrorById, [categoryId]: msg },
      error: msg,
    })),

  setSearchLoading: (cacheKey, v) =>
    set((state) => ({
      searchLoadingByKey: { ...state.searchLoadingByKey, [cacheKey]: v },
      isLoading: v,
    })),

  setSearchError: (cacheKey, msg) =>
    set((state) => ({
      searchErrorByKey: { ...state.searchErrorByKey, [cacheKey]: msg },
      error: msg,
    })),

  setDetailLoading: (uuid, v) =>
    set((state) => ({
      detailLoadingByUuid: { ...state.detailLoadingByUuid, [uuid]: v },
      isLoading: v,
    })),

  setDetailError: (uuid, msg) =>
    set((state) => ({
      detailErrorByUuid: { ...state.detailErrorByUuid, [uuid]: msg },
      error: msg,
    })),

  // ---- dashboard ----
  setFromApi: (payload, authKey) => {
    // Qibla relies on a device compass / geolocation, so the tile is mobile-only.
    // Halal Scanner needs the camera + on-device OCR — the web app has no such
    // screen, so its tile would only ever land on "Coming soon".
    // Drop both at the data boundary so no downstream view has to special-case them.
    const MOBILE_ONLY_LINKS = ["qibla", "halal_scanner", "scanner"];
    const MOBILE_ONLY_NAMES = ["qibla", "halal scanner"];
    const incomingCategories = payload.categories ?? [];
    const webCategories = incomingCategories.filter(
      (c) =>
        !MOBILE_ONLY_LINKS.includes(c?.link ?? "") &&
        !MOBILE_ONLY_NAMES.includes((c?.name ?? "").trim().toLowerCase()),
    );

    set({
      data: payload,
      main_banners: payload.main_banners ?? [],
      categories: webCategories,
      sub_categories: payload.sub_categories ?? [],
      ummah_posts: payload.ummah_posts ?? null,
      sub_banners: payload.sub_banners ?? [],
      related_services: normalizeRelatedServices(payload.related_services ?? []),
      // 서버 키는 notice_dialog 다 — home_notice 만 읽던 탓에 공지가 웹에서
      // 한 번도 채워지지 않았다. 옛 키도 같이 받아둔다.
      home_notice: payload.notice_dialog ?? payload.home_notice ?? null,
      new_notice: !!payload.new_notice,

      dashboardAuthKey: authKey,
      hasDashboard: true,

      dashboardError: null,
      error: null,
    });
  },

  setSearchHome: (keywords, histories) =>
    set({ recommended_keywords: keywords, search_histories: histories }),

  removeSearchHistories: (keywords) =>
    set((state) => ({
      search_histories: keywords?.length
        ? state.search_histories.filter((h) => !keywords.includes(h))
        : [],
    })),

  clear: () =>
    set({
      ...emptyState,
      isLoading: false,
      error: null,
    }),

  getActiveMainBanners: () => get().main_banners.filter((b) => b.status),
  getActiveSubBanners: () => get().sub_banners.filter((b) => b.status),
  getActiveCategories: () => get().categories.filter((c) => c.status),
  getSubCategoriesByCategoryId: (categoryId) =>
    get().sub_categories.filter((s) => s.category_id === categoryId && s.status),

  // ---- category ----
  setCategoryServices: (categoryId, authKey, payload) => {
    set((state) => ({
      category_services_by_id: {
        ...state.category_services_by_id,
        [categoryId]: {
          authKey,
          data: {
            search_filter: {
              location_tags: payload.search_filter?.location_tags ?? [],
              min_price: payload.search_filter?.min_price ?? null,
              max_price: payload.search_filter?.max_price ?? null,
              count: payload.search_filter?.count ?? 0,
            },
            related_services: normalizeRelatedServices(payload.related_services ?? []),
          },
          fetchedAt: Date.now(),
        },
      },
    }));
  },

  clearCategoryServices: () => set({ category_services_by_id: {} }),

  // ---- search ----
  hasSearchServices: (cacheKey, authKey) => {
    const item = get().search_services_by_key?.[cacheKey];
    return !!item && item.authKey === authKey;
  },

  getSearchServices: (cacheKey) => {
    const item = get().search_services_by_key?.[cacheKey];
    return item?.data ?? null;
  },

  canFetchMoreSearchServices: (cacheKey) => {
    const data = get().search_services_by_key?.[cacheKey]?.data;
    if (!data) return true;
    const { current_page, total_pages } = data.pagination ?? { current_page: 0, total_pages: 0 };
    return current_page < total_pages;
  },

  upsertSearchServices: (cacheKey, authKey, payload, page) => {
    set((state) => {
      const prev = state.search_services_by_key?.[cacheKey];

      if (page <= 1 || !prev) {
        return {
          search_services_by_key: {
            ...state.search_services_by_key,
            [cacheKey]: { authKey, data: payload, fetchedAt: Date.now() },
          },
        };
      }

      const prevServices = prev.data.services ?? [];
      const nextServices = payload.services ?? [];

      const map = new Map<string, ServiceSearchItem>();
      for (const s of prevServices) map.set(s.uuid, s);
      for (const s of nextServices) map.set(s.uuid, s);

      return {
        search_services_by_key: {
          ...state.search_services_by_key,
          [cacheKey]: {
            authKey,
            data: {
              search_filter: payload.search_filter,
              pagination: payload.pagination,
              services: Array.from(map.values()),
            },
            fetchedAt: Date.now(),
          },
        },
      };
    });
  },

  clearSearchServices: () => set({ search_services_by_key: {} }),

  // ---- detail ----
  hasServiceDetail: (uuid, authKey) => {
    const item = get().service_detail_by_uuid?.[uuid];
    return !!item && item.authKey === authKey;
  },

  getServiceDetail: (uuid) => get().service_detail_by_uuid?.[uuid]?.data ?? null,

  setServiceDetail: (uuid, authKey, payload) => {
    set((state) => ({
      service_detail_by_uuid: {
        ...state.service_detail_by_uuid,
        [uuid]: { authKey, data: payload, fetchedAt: Date.now() },
      },
    }));
  },

  clearServiceDetail: () => set({ service_detail_by_uuid: {} }),

  // 캐시 키에 통화가 없어, 통화를 바꿔도 재조회가 캐시에 막혀 이전 통화
  // 금액이 그대로 보였다. 키마다 통화를 끼워 넣는 대신 여기서 한 번에 비운다 —
  // hasDashboard=false 는 useEnsureDashboard 가 구독하고 있어 홈도 자동 재조회된다.
  clearPricedCaches: () =>
    set({
      hasDashboard: false,
      category_services_by_id: {},
      search_services_by_key: {},
      service_detail_by_uuid: {},
    }),
}));
